import React, { useEffect, useRef, useState } from 'react';
import { BotResponse } from './BotResponse';
import './triplex-chat-widget.scss';

type Message = {
  text: string;
  from: 'user' | 'bot';
  chatId?: string;
  feedback?: 'like' | 'dislike';
  feedbackLoading?: boolean;
};

const EXAMPLES = [
  'Что такое Triplex?',
  'Как начать использовать Triplex?',
  'Какой компонент использовать для ввода телефона?',
  'Каким компонентом выводить ошибки или уведомления?',
  'Приведи пример кода формы с валидацией',
];

const API_URL = 'https://triplex-dev.ru/orbit-api';
const YOUR_PUBLIC_API_KEY = '52f89e52c505b466d9cf67457750287a34fa2a14e9d541b432c1d4d0669c8dc2';

const TriplexChatWindow: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const listRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  const scrollToBottom = () => {
    // Плавный скролл к последнему сообщению
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const callApi = async (question: string) => {
    const res = await fetch(API_URL + '/api/ask', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        apiKey: YOUR_PUBLIC_API_KEY,
        userId: 'web-widget',
      }),
    });
    if (!res.ok) {
      throw new Error('Ошибка запроса');
    }
    return res.json();
  };

  const send = async (preset?: string) => {
    if (loading) return; // защита от дабл-кликов
    const textToSend = (preset ?? input).trim();
    if (!textToSend) return;

    // 1) добавляем сообщение пользователя
    setMessages((m) => [...m, { text: textToSend, from: 'user' }]);
    setInput('');
    setLoading(true);

    try {
      const data = await callApi(textToSend);

      const answer = data.answer ?? 'Не удалось сформировать ответ.';
      setMessages((m) => [...m, { text: answer, from: 'bot', chatId: data.chatId }]);
    } catch (e: any) {
      setMessages((m) => [
        ...m,
        { text: 'Упс, не получилось получить ответ. Попробуйте ещё раз.', from: 'bot' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const sendFeedback = async (msgIndex: number, fb: 'like' | 'dislike') => {
    setMessages((arr) => {
      const copy = [...arr];
      const m = copy[msgIndex];
      if (!m.chatId || m.feedbackLoading) return arr;
      copy[msgIndex] = { ...m, feedbackLoading: true, feedback: fb }; // оптимистично
      return copy;
    });

    try {
      await fetch(`${API_URL}/api/feedback`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apiKey: YOUR_PUBLIC_API_KEY,
          chatId: messages[msgIndex].chatId,
          feedback: fb,
        }),
      });
    } catch (e) {
      // откатим, если не получилось
      setMessages((arr) => {
        const copy = [...arr];
        const m = copy[msgIndex];
        copy[msgIndex] = { ...m, feedback: undefined };
        return copy;
      });
      console.warn('Не удалось отправить фидбек', e);
    } finally {
      setMessages((arr) => {
        const copy = [...arr];
        const m = copy[msgIndex];
        copy[msgIndex] = { ...m, feedbackLoading: false };
        return copy;
      });
    }
  };


  return (
    <div className="triplex-ai-window">
      <div className="triplex-ai-header">
        <span>
          AI-ассистент
        </span>
        <button type="button" onClick={onClose} className="triplex-ai-close-btn">×</button>
      </div>

      {/* Быстрые примеры (показываем, если нет сообщений) */}
      {messages.length === 0 && (
        <div className="triplex-ai-quick">
          <div className="triplex-ai-quick-title">Попробуйте спросить:</div>
          <div className="triplex-ai-quick-list">
            {EXAMPLES.map((q) => (
              <button
                key={q}
                type="button"
                className="triplex-ai-quick-chip"
                onClick={() => send(q)}
                disabled={loading}
              >
                {q}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="triplex-ai-messages" ref={listRef}>
        {messages.map((msg, i) => (
          <div key={i} className={`triplex-ai-message ${msg.from}`}>
            <span className={`bubble ${msg.from}`}>
              {msg.from === 'bot' ? <BotResponse text={msg.text} /> : <p>{msg.text}</p>}
              {msg.from === 'bot' && msg.chatId && (
                <div className="ai-feedback">
                  <button
                    className={`fb-btn like ${msg.feedback === 'like' ? 'active' : ''}`}
                    onClick={() => sendFeedback(i, 'like')}
                    disabled={!!msg.feedback || msg.feedbackLoading}
                    title="Полезно"
                  >
                    👍 {msg.feedback === 'like' ? 'Спасибо!' : 'Полезно'}
                  </button>
                  <button
                    className={`fb-btn dislike ${msg.feedback === 'dislike' ? 'active' : ''}`}
                    onClick={() => sendFeedback(i, 'dislike')}
                    disabled={!!msg.feedback || msg.feedbackLoading}
                    title="Не помогло"
                  >
                    👎 {msg.feedback === 'dislike' ? 'Исправим' : 'Не помогло'}
                  </button>
                </div>
              )}
            </span>
          </div>
        ))}

        {/* Лоадер ответа бота */}
        {loading && (
          <div className="triplex-ai-message bot">
            <span className="bubble bot">
              <span className="typing">
                <span className="dot" />
                <span className="dot" />
                <span className="dot" />
              </span>
            </span>
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      <div className="triplex-ai-footer">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') send();
          }}
          placeholder="Спросите про компоненты, паттерны, иконки…"
          className="triplex-ai-input"
          disabled={loading}
        />
        <button
          type="button"
          onClick={() => send()}
          className="triplex-ai-send-btn"
          disabled={!input.trim() || loading}
        >
          {loading ? 'Ждём…' : 'Отправить'}
        </button>
      </div>
    </div>
  );
};

export default TriplexChatWindow;